import { form, a2pConsentEnabled, businessName } from "../../../js/utils/variables";
import { t } from "../../utils/i18n";
import validator from "validator";

const escapeHtml = (v) => String(v).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

const renderUIEditInfoLead = (lead) => {
    const container = document.querySelector('.verifyInformation__container')
    if (!container) return

    const card = container.querySelector('.card');
    card.innerHTML = "";

    const languageDropdownHTML = form?.show_language_dropdown ? `
        <div class="info__lead verify-infor__input language-input">
            <label>${t('preferred_language')}</label>
            <div class="language__dropdown-verify"></div>
        </div>
    ` : '';

    // Book-first flow: when the channel is set to "ask for DOB" but does NOT
    // collect patient info first, DOB has no earlier step to be gathered in, so
    // it must appear here on the verify-info page. The #dobDatepicker flatpickr
    // is initialized in editInfoListener(); the value is submitted from
    // onClickContinueBtn (verifyInforListenters.js).
    const dobHTML = (form?.appointment_channel?.collect_dob
        || (form?.show_dob_field && form?.appointment_channel?.collect_patient_information_first_before_scheduling)) ? `
        <div class="info__lead verify-infor__input dob-input">
            <label>${t('dob')} <span class='text__required'>* Required</span></label>
            <input id="dobDatepicker" type="text" placeholder="${t('select_dob')}" readonly />
        </div>
    ` : '';

    // Gender is never asked on the upfront prebook patient-info form (same
    // conversion reasoning as DOB above), so both drivers (channel
    // `collect_gender` and the widget-row `show_gender_field` on the prebook
    // flow) collect it here instead. Submitted from onClickContinueBtn.
    const genderOptions = ['male', 'female', 'other', 'prefer_not_to_say'];
    const genderHTML = (form?.appointment_channel?.collect_gender
        || (form?.show_gender_field && form?.appointment_channel?.collect_patient_information_first_before_scheduling)) ? `
        <div class="info__lead verify-infor__input gender-input">
            <label>${t('gender')} <span class='text__required'>* Required</span></label>
            <select id="genderVerify" aria-label="${t('gender')}">
                <option value="" disabled ${lead?.gender ? '' : 'selected'}>${t('select_gender')}</option>
                ${genderOptions.map((g) => `<option value="${g}" ${lead?.gender === g ? 'selected' : ''}>${t(g)}</option>`).join('')}
            </select>
        </div>
    ` : '';

    const practiceNameText = businessName || '[Business Name]';

    let consentHTML = '';
    if (a2pConsentEnabled) {
        consentHTML = `
            <div class="note__wrapper sms-consent__wrapper verify-info-consent">
                <div class="consent-row sms-consent-transactional-row">
                    <span class="noteOption__checkbox sms-consent-transactional-checkbox" role="button" tabindex="0" aria-label="Transactional and informational SMS consent"><i class="ri-check-line"></i></span>
                    <label class="sms-consent-label">I agree to receive text messages and/or emails from <span class="sms-consent-business-name">${practiceNameText}</span> about my appointments and care (e.g. confirmations, reminders and replies to my questions).</label>
                </div>
                <div class="consent-row terms-privacy-row">
                    <span class="noteOption__checkbox terms-privacy-checkbox" role="button" tabindex="0" aria-label="Accept Terms and Conditions and Privacy Policy"><i class="ri-check-line"></i></span>
                    <label class="sms-consent-label terms-privacy-label">I accept the <u class="link terms-link">Terms and Conditions</u> and <u class="link privacy-link">Privacy Policy</u>.</label>
                </div>
                <div class="consent-row sms-consent-promotional-row">
                    <span class="noteOption__checkbox sms-consent-promotional-checkbox" role="button" tabindex="0" aria-label="Promotional and marketing SMS consent"><i class="ri-check-line"></i></span>
                    <label class="sms-consent-label">I agree to receive promotional text messages and/or emails from <span class="sms-consent-business-name">${practiceNameText}</span>. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe, HELP for help. Consent is not a condition of purchase.</label>
                </div>
                <p class="sms-consent-disclaimer">Message and data rates may apply. Message frequency varies. Reply STOP to unsubscribe, HELP for help.</p>
            </div>
        `;
    }

    // Collapse the contact rows the lead record already answers into a compact
    // read-only summary (with an Edit escape hatch) so the visitor only sees
    // the fields that still need input (typically just DOB on the late-DOB
    // detour). Collapse only when every required field is prefilled AND valid —
    // a hidden invalid value would dead-end the Continue button with no visible
    // error. The inputs stay in the DOM (display:none) because the submit and
    // validation paths read them positionally.
    const prefilledCollapsible = Boolean(
        lead?.first_name && lead?.last_name && lead?.phone
        && (!lead?.email || validator.isEmail(String(lead.email).trim()))
    );
    const summaryHTML = prefilledCollapsible ? `
        <div class="verify-infor__summary">
            <div class="verify-infor__summary-fields">
                <div class="info__lead"><label>Full Name</label><span>${escapeHtml([lead.first_name, lead.last_name].filter(Boolean).join(' '))}</span></div>
                <div class="info__lead"><label>Phone number</label><span>${escapeHtml(lead.phone)}</span></div>
                ${lead?.email ? `<div class="info__lead"><label>Email address</label><span>${escapeHtml(lead.email)}</span></div>` : ''}
            </div>
            <button type="button" class="edit-info__btn verify-infor-edit__btn" aria-label="Edit your information"><i class="ri-pencil-line"></i> Edit</button>
        </div>
    ` : '';

    card.insertAdjacentHTML('afterbegin', `
        <div class="verify-infor__body edit ${prefilledCollapsible ? 'prefilled-collapsed' : ''}">
            ${summaryHTML}
            <div class="info__lead verify-infor__input full_name-input ${!lead?.first_name || !lead?.last_name ? "invalid" : ""}">
                <label>Full Name <span class='text__required'>* Required</span></label>
                <input
                    type="text"
                    placeholder="Enter your full name"
                    value="${escapeHtml([lead?.first_name, lead?.last_name].filter(Boolean).join(' '))}"
                />

            </div>
            <div class="info__lead verify-infor__input email-input">
                <label>Email address</label>
                <input type="text" placeholder="Enter your email address" value="${lead?.email ? escapeHtml(lead.email) : ""}" />
            </div>
            <div class="info__lead verify-infor__input phone-input ${!lead?.phone ? "invalid" : ""}">
                <label>Phone number <span class='text__required'>* Required</span></label>
                <input type="text" placeholder="Enter your phone number" value="${lead?.phone ? lead?.phone : ""}" />
            </div>
            ${dobHTML}
            ${genderHTML}
            ${languageDropdownHTML}
            ${consentHTML}
        </div>
    `)
}

export {
    renderUIEditInfoLead
}
